import { Fragment, Node, NodeType, Slice } from 'prosemirror-model';

// Copies a single path down a node (depth first) and fills the bottom node with `content`. The bottom node must be empty
export function copyAndFillNode(node: Node, content: Fragment): Node {
  if (node.content.childCount > 0) {
    return node.copy(Fragment.from(copyAndFillNode(node.content.firstChild, content)));
  } else {
    return node.copy(content);
  }
}

export function removeNodeType(node: Node, nodeTypes: NodeType[]): Node {
  if (nodeTypes.includes(node.type)) {
    return null;
  }

  return node.copy(removeNodeTypeFromFragment(node.content, nodeTypes));
}

export function removeNodeTypeFromFragment(fragment: Fragment, nodeTypes: NodeType[]): Fragment {
  const newNodes: Node[] = [];

  fragment.forEach((node: Node) => {
    const newNode = removeNodeType(node, nodeTypes);
    if (newNode) {
      newNodes.push(newNode);
    }
  });

  return Fragment.fromArray(newNodes);
}

export function removeNodeTypeFromSlice(slice: Slice, nodeTypes: NodeType[]): Slice {
  return new Slice(removeNodeTypeFromFragment(slice.content, nodeTypes), slice.openStart, slice.openEnd);
}

/**
 * Returns a copy of a node.
 * @param node The node to copy.
 * @param cloner A function to copy the node - could be used to change parts of the node before its copied.
 * @returns The copied node.
 */
export function cloneNode(node: Node, cloner: (node: Node, content: Fragment) => Node): Node {
  return node ? cloner(node, node.content ? cloneFragment(node.content, cloner) : null) : null;
}

/**
 * Returns a copy of a fragment.
 * @param fragment The fragment to copy.
 * @param cloner A function to copy the nodes in the fragment - could be used to change parts of the nodes before its copied.
 * @returns The copied fragment.
 */
export function cloneFragment(fragment: Fragment, cloner: (node: Node, content: Fragment) => Node): Fragment {
  if (!fragment) {
    return null;
  }

  const nodes: Node[] = [];
  fragment.forEach(node => {
    nodes.push(cloneNode(node, cloner));
  });

  return Fragment.fromArray(nodes);
}

/**
 * Returns a copy of a slice.
 * @param slice The slice to copy.
 * @param cloner A function to copy the nodes in the slice - could be used to change parts of the nodes before its copied.
 * @returns The copied slice.
 */
export function cloneSlice(slice: Slice, cloner: (node: Node, content: Fragment) => Node) {
  return slice ? new Slice(cloneFragment(slice.content, cloner), slice.openStart, slice.openEnd) : null;
}
